import { z } from 'zod';
import {
  HttpError,
  MiceActivityPageSchema,
  MiceBudgetViewSchema,
  MiceClientOptionsSchema,
  MiceImportPreviewSchema,
  MiceImportResultSchema,
  MiceDebitCompanyOptionsSchema,
  MiceEventPremisesSchema,
  MiceFlightOptionsSchema,
  MiceNeedPageSchema,
  MiceNeedsOrderResultSchema,
  MiceNeedViewSchema,
  MiceOrderActionResultSchema,
  MiceOrderViewSchema,
  MiceOrderVouchersSchema,
  MiceOrdersPageSchema,
  MiceProfileBulkResultSchema,
  MiceProjectListSchema,
  MiceProjectViewSchema,
  MiceTravelerCostCenterViewSchema,
  MiceTravelerCostCenterOptionsSchema,
  MiceTravelerCostCentersSchema,
  MiceTravelerViewSchema,
  MiceTravelersPageSchema,
  MiceRequesterOptionsSchema,
  MiceWorkspaceOptionsSchema,
  type CreateMiceProjectRequest,
  type MiceActivityPage,
  type MiceActivityQuery,
  type MiceBudgetView,
  type MiceImportPreview,
  type MiceImportResult,
  type MiceClientOption,
  type MiceDebitCompanyOptions,
  type MiceEventPremises,
  type MiceChangeDateRequest,
  type MiceChooseInput,
  type MiceFlightOptions,
  type MiceNeedPage,
  type MiceNeedsOrderRequest,
  type MiceTravelersPage,
  type MiceTravelersQuery,
  type MiceNeedsOrderResult,
  type MiceNeedsQuery,
  type MiceNeedView,
  type MicePremisesRequest,
  type MiceOrdersPage,
  type MiceOrderVouchers,
  type MiceOrdersQuery,
  type MiceProfileBulkResolve,
  type MiceProfileBulkResult,
  type MiceProfileResolve,
  type MiceProjectView,
  type MiceSetTravelerCostCenterRequest,
  type MiceTraveler,
  type MiceTravelerCostCenterOptions,
  type MiceTravelerCostCenters,
  type MiceTravelerCostCenterView,
  type MiceRequesterOption,
  type MiceWorkspaceOptions,
} from '@voll-intelligence/domain';
import { env } from '@web/core';
import type { AuthService, HttpService } from '@web/data/services';
import { trimTrailingSlashes } from '@web/data/services/url';

/** Eventos por página do feed de atividade. */
const ACTIVITY_PAGE_SIZE = 50;

const ProfileResolvedSchema = z.object({ traveler: MiceTravelerViewSchema });

export interface MiceRepository {
  searchClients: (orgId: string, q: string) => Promise<MiceClientOption[]>;
  workspaceOptions: (
    orgId: string,
    workspaceId: string,
    clientId: string,
  ) => Promise<MiceWorkspaceOptions>;
  debitCompanyOptions: (
    orgId: string,
    workspaceId: string,
    clientId: string,
    companyId: string,
  ) => Promise<MiceDebitCompanyOptions>;
  searchRequesters: (
    orgId: string,
    workspaceId: string,
    clientId: string,
    q: string,
  ) => Promise<MiceRequesterOption[]>;
  createProject: (orgId: string, body: CreateMiceProjectRequest) => Promise<MiceProjectView>;
  listProjects: (orgId: string) => Promise<MiceProjectView[]>;
  getProject: (orgId: string, projectId: string) => Promise<MiceProjectView>;
  deleteProject: (orgId: string, projectId: string) => Promise<void>;
  savePremises: (
    orgId: string,
    projectId: string,
    body: MicePremisesRequest,
  ) => Promise<MiceProjectView>;
  extractPremises: (
    orgId: string,
    projectId: string,
    premisesText: string,
  ) => Promise<MiceEventPremises>;
  listOrders: (orgId: string, projectId: string, query: MiceOrdersQuery) => Promise<MiceOrdersPage>;
  listOrderVouchers: (
    orgId: string,
    projectId: string,
    orderId: string,
  ) => Promise<MiceOrderVouchers>;
  queue: (orgId: string, projectId: string, orderIds: string[]) => Promise<number>;
  unqueue: (orgId: string, projectId: string, orderIds: string[]) => Promise<number>;
  release: (orgId: string, projectId: string, orderIds: string[]) => Promise<number>;
  remove: (orgId: string, projectId: string, orderIds: string[]) => Promise<number>;
  /** O e-mail corrigido da linha sem viajante (necessidade ou pedido): ela volta à validação. */
  correctEmail: (
    orgId: string,
    projectId: string,
    row: MiceEmailRow,
    email: string,
  ) => Promise<void>;
  /** Analisa a planilha (formato, válidas, com problema, duplicadas) sem gravar nada. */
  previewImport: (orgId: string, projectId: string, file: File) => Promise<MiceImportPreview>;
  /** Grava a prévia pelo token (409 se venceu ou já foi confirmada). */
  confirmImport: (orgId: string, projectId: string, token: string) => Promise<MiceImportResult>;
  downloadTemplate: (orgId: string) => Promise<Blob>;
  listNeeds: (orgId: string, projectId: string, query: MiceNeedsQuery) => Promise<MiceNeedPage>;
  /** Viajantes (necessidade + pedidos) filtrados por fase e busca, paginados no servidor. */
  listTravelers: (
    orgId: string,
    projectId: string,
    query: MiceTravelersListQuery,
  ) => Promise<MiceTravelersPage>;
  /**
   * Feed da compra, do mais novo para o mais antigo; `before` é o cursor da página anterior.
   * `filter` restringe ao viajante (`email`) e às decisões do cadastro (`kinds`): o histórico do
   * painel dele.
   */
  listActivity: (
    orgId: string,
    projectId: string,
    before: string | null,
    filter?: Pick<MiceActivityQuery, 'email' | 'kinds'>,
  ) => Promise<MiceActivityPage>;
  /** Decisão do operador sobre o cadastro de um viajante (`need.id` ou `order:<id>`). */
  resolveProfile: (
    orgId: string,
    projectId: string,
    travelerId: string,
    body: MiceProfileResolve,
  ) => Promise<MiceTraveler>;
  /** Centro de custo de cada viajante do evento (só quem tem o próprio vem em `items`). */
  travelerCostCenters: (orgId: string, projectId: string) => Promise<MiceTravelerCostCenters>;
  /** O que o seletor oferece: a lista com que o servidor confere a troca. */
  travelerCostCenterOptions: (
    orgId: string,
    projectId: string,
  ) => Promise<MiceTravelerCostCenterOptions>;
  /** O operador troca o centro de custo de um viajante (409 se a compra já começou). */
  setTravelerCostCenter: (
    orgId: string,
    projectId: string,
    body: MiceSetTravelerCostCenterRequest,
  ) => Promise<MiceTravelerCostCenterView>;
  /** "Gravar dados da planilha" / "Manter cadastro da VOLL" em lote. */
  resolveProfiles: (
    orgId: string,
    projectId: string,
    body: MiceProfileBulkResolve,
  ) => Promise<MiceProfileBulkResult>;
  downloadNeedsTemplate: (orgId: string) => Promise<Blob>;
  recommendNeeds: (orgId: string, projectId: string, needIds: string[]) => Promise<number>;
  approveNeeds: (orgId: string, projectId: string, needIds: string[]) => Promise<number>;
  markNeedsManual: (orgId: string, projectId: string, needIds: string[]) => Promise<number>;
  reconsiderNeed: (
    orgId: string,
    projectId: string,
    needId: string,
    comment: string,
  ) => Promise<MiceNeedView>;
  /** Nova data de um sentido: a necessidade volta ao agente, que busca na data nova. */
  changeNeedDate: (
    orgId: string,
    projectId: string,
    needId: string,
    change: MiceChangeDateRequest,
  ) => Promise<MiceNeedView>;
  /** Voo preterido da análise: o servidor registra (202) e o worker cota a nova versão. */
  chooseFlight: (
    orgId: string,
    projectId: string,
    needId: string,
    choice: MiceChooseInput,
  ) => Promise<MiceNeedView>;
  /** Os voos da faixa do evento que a busca da versão vigente gravou, de um sentido. */
  listFlightOptions: (
    orgId: string,
    projectId: string,
    needId: string,
    direction: 'OUTWARD' | 'RETURN',
  ) => Promise<MiceFlightOptions>;
  /**
   * Compra as aprovadas: pelos ids (`skip` só vai no corpo quando há sentido tirado) ou todas as
   * verdes (`allGreen`, sem lista: o servidor lê as verdes, recomendadas ou aprovadas, na hora).
   */
  orderNeeds: (
    orgId: string,
    projectId: string,
    target: MiceOrderTarget,
  ) => Promise<MiceNeedsOrderResult>;
  exportOrders: (orgId: string, projectId: string) => Promise<Blob>;
  /** Confere as premissas e pede a cotação de todo o evento; devolve o projeto. */
  generateBudget: (orgId: string, projectId: string) => Promise<MiceProjectView>;
  getBudget: (orgId: string, projectId: string) => Promise<MiceBudgetView>;
  /** A planilha e o nome que o servidor lhe deu (`null` sem `Content-Disposition`). */
  exportBudget: (
    orgId: string,
    projectId: string,
  ) => Promise<{ blob: Blob; fileName: string | null }>;
}

/** Multipart e download não passam pelo HttpService (ele fixa JSON). */
async function rawFetch(
  url: string,
  authService: Pick<AuthService, 'getAccessToken'>,
  init: RequestInit,
): Promise<Response> {
  const token = await authService.getAccessToken();
  const headers: Record<string, string> = { Accept: '*/*' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(url, { ...init, headers, credentials: 'omit' });
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => undefined);
    const payload =
      body && typeof body === 'object' ? (body as Record<string, unknown>) : undefined;
    throw new HttpError(
      typeof payload?.message === 'string' ? payload.message : res.statusText,
      res.status,
      payload,
    );
  }
  return res;
}

const orgPath = (orgId: string): string => `/orgs/${encodeURIComponent(orgId)}/mice`;
const workspacePath = (orgId: string, workspaceId: string): string =>
  `${orgPath(orgId)}/workspaces/${encodeURIComponent(workspaceId)}`;
const projectPath = (orgId: string, projectId: string): string =>
  `${orgPath(orgId)}/projects/${encodeURIComponent(projectId)}`;

/** Página do feed: o cursor e, no histórico do viajante, o e-mail e só as decisões do cadastro. */
const activityQuery = (
  before: string | null,
  filter: Pick<MiceActivityQuery, 'email' | 'kinds'> | undefined,
) => ({
  limit: ACTIVITY_PAGE_SIZE,
  before: before ?? undefined,
  email: filter?.email,
  kinds: filter?.kinds,
});

/** Decisões do cadastro do viajante: uma por viajante e as ações em lote da seleção. */
function profileMethods(
  http: HttpService,
): Pick<MiceRepository, 'resolveProfile' | 'resolveProfiles'> {
  return {
    resolveProfile: async (orgId, projectId, travelerId, body) =>
      (
        await http.post(
          `${projectPath(orgId, projectId)}/travelers/${encodeURIComponent(travelerId)}/profile`,
          body,
          { schema: ProfileResolvedSchema },
        )
      ).traveler,
    resolveProfiles: (orgId, projectId, body) =>
      http.post(`${projectPath(orgId, projectId)}/profiles/resolve`, body, {
        schema: MiceProfileBulkResultSchema,
      }),
  };
}

const needPath = (orgId: string, projectId: string, needId: string): string =>
  `${projectPath(orgId, projectId)}/needs/${encodeURIComponent(needId)}`;

/** O operador e a sugestão de uma necessidade: reconsiderar, escolher outro voo e a lista da faixa
 *  do evento em que escolher. */
function needChoiceMethods(
  http: HttpService,
): Pick<
  MiceRepository,
  'reconsiderNeed' | 'changeNeedDate' | 'chooseFlight' | 'listFlightOptions'
> {
  return {
    reconsiderNeed: (orgId, projectId, needId, comment) =>
      http.post(
        `${needPath(orgId, projectId, needId)}/reconsider`,
        { comment },
        { schema: MiceNeedViewSchema },
      ),
    changeNeedDate: (orgId, projectId, needId, change) =>
      http.post(`${needPath(orgId, projectId, needId)}/date`, change, {
        schema: MiceNeedViewSchema,
      }),
    chooseFlight: (orgId, projectId, needId, choice) =>
      http.post(`${needPath(orgId, projectId, needId)}/choose`, choice, {
        schema: MiceNeedViewSchema,
      }),
    listFlightOptions: (orgId, projectId, needId, direction) =>
      http.get(`${needPath(orgId, projectId, needId)}/flight-options`, {
        query: { direction },
        schema: MiceFlightOptionsSchema,
      }),
  };
}

/** O e-mail corrigido de uma linha importada, necessidade ou pedido de planilha. */
function emailMethods(http: HttpService): Pick<MiceRepository, 'correctEmail'> {
  return {
    correctEmail: async (orgId, projectId, row, email) => {
      if (row.kind === 'need') {
        await http.post(
          `${needPath(orgId, projectId, row.id)}/email`,
          { email },
          { schema: MiceNeedViewSchema },
        );
        return;
      }
      await http.post(
        `${projectPath(orgId, projectId)}/orders/${encodeURIComponent(row.id)}/email`,
        { email },
        { schema: MiceOrderViewSchema },
      );
    },
  };
}

/** A linha importada cujo e-mail é corrigido. */
export interface MiceEmailRow {
  kind: 'need' | 'order';
  id: string;
}

/** O que a compra leva: as necessidades escolhidas ou, sem lista, todas as verdes do evento. */
export type MiceOrderTarget =
  | { needIds: string[]; skip?: MiceNeedsOrderRequest['skip'] }
  | { allGreen: true };

/** A cor é opcional: quem não filtra por ela não a manda. */
type MiceTravelersListQuery = Omit<MiceTravelersQuery, 'color'> & {
  color?: MiceTravelersQuery['color'];
};

/** Filtros dos viajantes na URL: o padrão de cada um fica de fora; `email` é exato (o painel de um
 *  viajante fora do filtro), `q` é a busca livre. */
const travelersQuery = (query: MiceTravelersListQuery) => ({
  phase: query.phase,
  q: query.q === '' ? undefined : query.q,
  email: query.email,
  alert: query.alert === 'any' ? undefined : query.alert,
  origin: query.origin === 'any' ? undefined : query.origin,
  color: query.color === 'any' ? undefined : query.color,
  page: query.page,
  pageSize: query.pageSize,
});

/** O nome do arquivo no `Content-Disposition` (o CORS da API o expõe). */
const attachmentName = (res: Response): string | null =>
  /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? null;

/** O orçamento do evento: gerar (devolve o projeto), o resumo e a planilha. */
function budgetMethods(
  http: HttpService,
  raw: (path: string, init: RequestInit) => Promise<Response>,
): Pick<MiceRepository, 'generateBudget' | 'getBudget' | 'exportBudget'> {
  return {
    generateBudget: (orgId, projectId) =>
      http.post(`${projectPath(orgId, projectId)}/budget`, undefined, {
        schema: MiceProjectViewSchema,
      }),
    getBudget: (orgId, projectId) =>
      http.get(`${projectPath(orgId, projectId)}/budget`, { schema: MiceBudgetViewSchema }),
    exportBudget: async (orgId, projectId) => {
      const res = await raw(`${projectPath(orgId, projectId)}/budget.xlsx`, { method: 'GET' });
      return { blob: await res.blob(), fileName: attachmentName(res) };
    },
  };
}

/** A compra pelos ids: `skip` só vai no corpo quando há sentido tirado. */
const needsOrderBody = (target: Extract<MiceOrderTarget, { needIds: string[] }>) =>
  target.skip?.length
    ? { needIds: target.needIds, skip: target.skip }
    : { needIds: target.needIds };

export function createMiceRepository(
  http: HttpService,
  authService: Pick<AuthService, 'getAccessToken'>,
): MiceRepository {
  const base = trimTrailingSlashes(env.VITE_MICE_API_URL);

  const raw = (path: string, init: RequestInit): Promise<Response> =>
    rawFetch(`${base}${path}`, authService, init);

  const action =
    (name: string) =>
    async (orgId: string, projectId: string, orderIds: string[]): Promise<number> =>
      (
        await http.post(
          `${projectPath(orgId, projectId)}/orders/${name}`,
          { orderIds },
          { schema: MiceOrderActionResultSchema },
        )
      ).updated;

  const needAction =
    (name: string) =>
    async (orgId: string, projectId: string, needIds: string[]): Promise<number> =>
      (
        await http.post(
          `${projectPath(orgId, projectId)}/needs/${name}`,
          { needIds },
          { schema: MiceOrderActionResultSchema },
        )
      ).updated;

  const uploadFile = async (path: string, file: File): Promise<unknown> => {
    const form = new FormData();
    form.append('file', file);
    return (await raw(path, { method: 'POST', body: form })).json();
  };

  return {
    searchClients: async (orgId, q) =>
      (
        await http.get(`${orgPath(orgId)}/clients`, {
          query: { q },
          schema: MiceClientOptionsSchema,
        })
      ).items,
    workspaceOptions: (orgId, workspaceId, clientId) =>
      http.get(workspacePath(orgId, workspaceId) + '/options', {
        query: { clientId },
        schema: MiceWorkspaceOptionsSchema,
      }),
    debitCompanyOptions: (orgId, workspaceId, clientId, companyId) =>
      http.get(
        `${workspacePath(orgId, workspaceId)}/debit-companies/${encodeURIComponent(companyId)}/options`,
        { query: { clientId }, schema: MiceDebitCompanyOptionsSchema },
      ),
    searchRequesters: async (orgId, workspaceId, clientId, q) =>
      (
        await http.get(`${workspacePath(orgId, workspaceId)}/requesters`, {
          query: { q, clientId },
          schema: MiceRequesterOptionsSchema,
        })
      ).items,
    createProject: (orgId, body) =>
      http.post(`${orgPath(orgId)}/projects`, body, { schema: MiceProjectViewSchema }),
    listProjects: async (orgId) =>
      (await http.get(`${orgPath(orgId)}/projects`, { schema: MiceProjectListSchema })).items,
    getProject: (orgId, projectId) =>
      http.get(projectPath(orgId, projectId), { schema: MiceProjectViewSchema }),
    deleteProject: async (orgId, projectId) => {
      await http.delete(projectPath(orgId, projectId));
    },
    savePremises: (orgId, projectId, body) =>
      http.put(`${projectPath(orgId, projectId)}/premises`, body, {
        schema: MiceProjectViewSchema,
      }),
    extractPremises: (orgId, projectId, premisesText) =>
      http.post(
        `${projectPath(orgId, projectId)}/premises/extract`,
        { premisesText },
        { schema: MiceEventPremisesSchema },
      ),
    listOrders: (orgId, projectId, query) =>
      http.get(`${projectPath(orgId, projectId)}/orders`, {
        query: { page: query.page, pageSize: query.pageSize, status: query.status },
        schema: MiceOrdersPageSchema,
      }),
    listOrderVouchers: (orgId, projectId, orderId) =>
      http.get(`${projectPath(orgId, projectId)}/orders/${encodeURIComponent(orderId)}/vouchers`, {
        schema: MiceOrderVouchersSchema,
      }),
    queue: action('queue'),
    unqueue: action('unqueue'),
    release: action('release'),
    remove: action('delete'),
    previewImport: async (orgId, projectId, file) =>
      MiceImportPreviewSchema.parse(
        await uploadFile(`${projectPath(orgId, projectId)}/imports/preview`, file),
      ),
    confirmImport: (orgId, projectId, token) =>
      http.post(
        `${projectPath(orgId, projectId)}/imports/${encodeURIComponent(token)}`,
        {},
        { schema: MiceImportResultSchema },
      ),
    downloadTemplate: async (orgId) =>
      (await raw(`${orgPath(orgId)}/template.csv`, { method: 'GET' })).blob(),
    listNeeds: (orgId, projectId, query) =>
      http.get(`${projectPath(orgId, projectId)}/needs`, {
        query: { page: query.page, pageSize: query.pageSize, status: query.status },
        schema: MiceNeedPageSchema,
      }),
    listTravelers: (orgId, projectId, query) =>
      http.get(`${projectPath(orgId, projectId)}/travelers`, {
        query: travelersQuery(query),
        schema: MiceTravelersPageSchema,
      }),
    listActivity: (orgId, projectId, before, filter) =>
      http.get(`${projectPath(orgId, projectId)}/activity`, {
        query: activityQuery(before, filter),
        schema: MiceActivityPageSchema,
      }),
    ...profileMethods(http),
    travelerCostCenters: (orgId, projectId) =>
      http.get(`${projectPath(orgId, projectId)}/traveler-cost-centers`, {
        schema: MiceTravelerCostCentersSchema,
      }),
    travelerCostCenterOptions: (orgId, projectId) =>
      http.get(`${projectPath(orgId, projectId)}/traveler-cost-centers/options`, {
        schema: MiceTravelerCostCenterOptionsSchema,
      }),
    setTravelerCostCenter: (orgId, projectId, body) =>
      http.put(`${projectPath(orgId, projectId)}/traveler-cost-centers`, body, {
        schema: MiceTravelerCostCenterViewSchema,
      }),
    downloadNeedsTemplate: async (orgId) =>
      (await raw(`${orgPath(orgId)}/needs-template.csv`, { method: 'GET' })).blob(),
    recommendNeeds: needAction('recommend'),
    approveNeeds: needAction('approve'),
    markNeedsManual: needAction('manual'),
    ...needChoiceMethods(http),
    ...emailMethods(http),
    orderNeeds: (orgId, projectId, target) =>
      http.post(
        `${projectPath(orgId, projectId)}/needs/order`,
        'allGreen' in target ? { allGreen: true } : needsOrderBody(target),
        { schema: MiceNeedsOrderResultSchema },
      ),
    exportOrders: async (orgId, projectId) =>
      (await raw(`${projectPath(orgId, projectId)}/orders/export.csv`, { method: 'GET' })).blob(),
    ...budgetMethods(http, raw),
  };
}
